import { useCallback, useMemo } from "react";
import { useAppSelector } from "@store/hooks";
import {
  selectCurrentRectangles,
  selectViewedEvaluationMode,
} from "@store/selectors";
import { selectBaseView } from "@store/slices/viewSlice";
import {
  selectIsSeedingSample,
  selectIsTourRunning,
  selectOnboardingState,
} from "../state/onboardingSlice";
import { useOnboardingSave } from "../state/useOnboardingSave";
import { useVisibleAnchors } from "../anchors";
import { hints, HintConditions } from "../hints";
import CoachMark from "./CoachMark";

/**
 * Shows at most one contextual hint at a time, when the feature it
 * describes first becomes relevant.
 *
 * Hints stay out of the way of the linear onboarding: nothing fires
 * while the tour is running or sample content is still being created,
 * so the user is never reading two things at once.
 */
const HINT_TARGETS = hints.map((hint) => hint.target);

export default function HintHost() {
  const entries = useAppSelector(selectOnboardingState);
  const tourRunning = useAppSelector(selectIsTourRunning);
  const seeding = useAppSelector(selectIsSeedingSample);
  const baseView = useAppSelector(selectBaseView);
  const rectangleCount = useAppSelector(selectCurrentRectangles).length;
  const evaluationMode = useAppSelector(selectViewedEvaluationMode);
  const anchors = useVisibleAnchors(HINT_TARGETS);
  const save = useOnboardingSave();

  const conditions: HintConditions = useMemo(
    () => ({ baseView, rectangleCount, evaluationMode }),
    [baseView, rectangleCount, evaluationMode]
  );

  // First hint whose version isn't recorded, whose conditions hold, and
  // whose anchor is actually on screen. That last clause matters: a
  // hint that can't be placed must fall through to the next one rather
  // than sitting at the head of the queue blocking everything behind
  // it.
  const active = useMemo(
    () =>
      hints.find((hint) => {
        const entry = entries[hint.key];
        const done =
          !!entry &&
          entry.version >= hint.version &&
          !!(entry.dismissed_at || entry.completed_at);
        if (done) return false;
        if (hint.when && !hint.when(conditions)) return false;
        return !!anchors[hint.target];
      }),
    [entries, conditions, anchors]
  );

  const record = useCallback(
    (key: string, version: number) => {
      save(key, { version, dismissed_at: new Date().toISOString() });
    },
    [save]
  );

  const anchorEl = active ? anchors[active.target] : undefined;

  if (tourRunning || seeding || !active || !anchorEl) {
    return null;
  }

  return (
    <CoachMark
      key={active.key}
      anchorEl={anchorEl}
      title={active.title}
      body={active.body}
      placement={active.placement}
      onDismiss={() => record(active.key, active.version)}
      // Also record after it has been on screen long enough to read, so
      // a hint the user simply worked past doesn't reappear forever.
      onSeen={() => record(active.key, active.version)}
    />
  );
}
